import React, { useState, useMemo, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import { ExtractedQuestion, QuestionType } from '../../types'
import { config } from '../../config/environment'
import { getQuestionTypeLabel, QUESTION_TYPE_OPTIONS } from '../../utils/questionTypes'

// Extended interface for merged questions with dictionary data
interface MergedQuestion extends ExtractedQuestion {
  data_source?: 'dictionary' | 'questionnaire' | 'merged'
  dictionary_enhanced?: boolean
  warnings?: string[]
  source_comparison?: {
    question_id: { dictionary: string | null; questionnaire: string | null }
    question_text: { dictionary: string | null; questionnaire: string | null }
    question_type: { dictionary: string | null; questionnaire: string | null }
    answer_options: { dictionary: string[] | null; questionnaire: string[] | null }
    choice_details?: { dictionary: any[] | null; questionnaire: string[] | null }
  }
}

interface QuestionReviewStepProps {
  sessionId: string
  questions: ExtractedQuestion[]
  sectionOrder?: string[]
  onQuestionsUpdated?: (questions: ExtractedQuestion[]) => void
  onError?: (error: string) => void
  mergeWarnings?: Array<{
    type: string
    questionId?: string
    message: string
    severity: 'info' | 'warning' | 'error'
  }>
  mergeStats?: {
    totalQuestions: number
    dictionaryEnhanced: number
    questionnaireOnly: number
    dictionaryOnly: number
    validDictionaryUsed: boolean
  }
}

interface EditingQuestion extends ExtractedQuestion {
  isEditing?: boolean
  hasChanges?: boolean
  notes?: string
  isSelected?: boolean
}

export const QuestionReviewStep: React.FC<QuestionReviewStepProps> = ({
  sessionId,
  questions: initialQuestions,
  sectionOrder,
  onQuestionsUpdated,
  onError,
  mergeWarnings = [],
  mergeStats
}) => {
  const [questions, setQuestions] = useState<EditingQuestion[]>(
    initialQuestions.map(q => ({ ...q, isSelected: true }))
  )
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})
  const [newQuestion, setNewQuestion] = useState<Partial<ExtractedQuestion & { notes?: string; answer_options_raw?: string }>>({
    question_id: '',
    section: '',
    base: 'All',
    question_type: 'single_choice',
    question_text: '',
    answer_options: [],
    answer_options_raw: '',
    programming_logic: '',
    special_instructions: '',
    notes: ''
  })

  const questionTypes = QUESTION_TYPE_OPTIONS

  // Section expansion state
  const [sectionExpanded, setSectionExpanded] = useState<Record<string, boolean>>({})
  
  // Filter state
  const [questionFilter, setQuestionFilter] = useState<'all' | 'dictionary' | 'questionnaire'>('all')

  // Group questions by section
  const groupQuestionsBySection = (questions: EditingQuestion[]) => {
    const grouped: Record<string, EditingQuestion[]> = {}
    
    questions.forEach(question => {
      const section = question.section?.trim() || 'Other'
      if (!grouped[section]) {
        grouped[section] = []
      }
      grouped[section].push(question)
    })
    
    // Use sectionOrder from Phase 1 if available, otherwise fall back to alphabetical
    let sortedSections: string[]
    if (sectionOrder && sectionOrder.length > 0) {
      // Use the Phase 1 section order, but include any additional sections found
      const knownSections = sectionOrder.filter(section => grouped[section])
      const unknownSections = Object.keys(grouped)
        .filter(section => !sectionOrder.includes(section) && section !== 'Other')
        .sort() // Sort unknown sections alphabetically
      
      // Combine: known sections in order + unknown sections + "Other" last
      sortedSections = [...knownSections, ...unknownSections]
      if (grouped['Other']) {
        sortedSections.push('Other')
      }
    } else {
      // Fall back to alphabetical sort with "Other" last
      sortedSections = Object.keys(grouped).sort((a, b) => {
        if (a === 'Other') return 1
        if (b === 'Other') return -1
        return a.localeCompare(b)
      })
    }
    
    const result: Record<string, EditingQuestion[]> = {}
    sortedSections.forEach(section => {
      result[section] = grouped[section]
    })
    
    return result
  }

  // Filter questions based on selected filter
  const filteredQuestions = useMemo(() => {
    if (questionFilter === 'all') return questions
    
    if (questionFilter === 'dictionary') {
      return questions.filter(q => 
        (q as any).data_source === 'merged' || (q as any).data_source === 'dictionary'
      )
    }
    
    if (questionFilter === 'questionnaire') {
      return questions.filter(q => 
        (q as any).data_source === 'questionnaire' || !(q as any).data_source
      )
    }
    
    return questions
  }, [questions, questionFilter])

  const groupedQuestions = useMemo(() => {
    return groupQuestionsBySection(filteredQuestions)
  }, [filteredQuestions, sectionOrder])

  const toggleSectionExpanded = (sectionName: string) => {
    setSectionExpanded(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }))
  }

  const isSectionExpanded = (sectionName: string) => {
    return sectionExpanded[sectionName] !== false // Default to expanded
  }

  const getQuestionTypeColor = (type: QuestionType) => {
    const colors: Record<QuestionType, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
      'single_choice': 'primary',
      'multi_choice': 'secondary',
      'open_end_text': 'success',
      'open_end_video': 'warning',
      'numeric': 'info',
      'text_display': 'default' as any,
      'single_choice_grid': 'error',
      'multiple_choice_grid': 'warning'
    }
    return colors[type] || 'default'
  }

  const toggleQuestionSelection = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { ...q, isSelected: !q.isSelected, hasChanges: true }
        : q
    ))
    setHasUnsavedChanges(true)
  }

  const toggleAllQuestionsInSection = (sectionQuestions: EditingQuestion[], selectAll: boolean) => {
    const questionIds = sectionQuestions.map(q => q.question_id)
    setQuestions(prev => prev.map(q => 
      questionIds.includes(q.question_id) 
        ? { ...q, isSelected: selectAll, hasChanges: true }
        : q
    ))
    setHasUnsavedChanges(true)
  }


  const startEditing = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { ...q, isEditing: true }
        : { ...q, isEditing: false }
    ))
  }

  const cancelEditing = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { ...initialQuestions.find(iq => iq.question_id === questionId)!, isEditing: false }
        : q
    ))
  }

  // Simple local state for immediate UI feedback - no expensive operations
  const [localInputValues, setLocalInputValues] = useState<Record<string, Record<string, any>>>({})

  const updateQuestion = useCallback((questionId: string, field: keyof (ExtractedQuestion & { notes?: string }), value: any) => {
    console.log('⌨️ updateQuestion called:', { questionId, field, valueLength: typeof value === 'string' ? value.length : 'non-string' })
    
    // Just update the local input state - this is super fast
    setLocalInputValues(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }))
  }, [])

  const saveEditingValue = (questionId: string, field: keyof (ExtractedQuestion & { notes?: string }), value: any) => {
    console.log('💾 saveEditingValue called:', { questionId, field, valueLength: typeof value === 'string' ? value.length : 'non-string' })
    // Only update main questions array when user finishes editing
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { ...q, [field]: value, hasChanges: true }
        : q
    ))
    setHasUnsavedChanges(true)
    
    // Clear the local input value since it's now saved
    setLocalInputValues(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: undefined
      }
    }))
  }

  const getFieldValue = (question: EditingQuestion, field: keyof (ExtractedQuestion & { notes?: string })) => {
    // Return local input value if it exists, otherwise return the stored value
    return localInputValues[question.question_id]?.[field] ?? question[field]
  }

  const saveQuestion = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { ...q, isEditing: false, hasChanges: false }
        : q
    ))
  }

  const deleteQuestion = (questionId: string) => {
    setQuestions(prev => prev.filter(q => q.question_id !== questionId))
    setHasUnsavedChanges(true)
  }

  const validateQuestion = (question: Partial<ExtractedQuestion & { notes?: string }>) => {
    const errors: string[] = []
    
    if (!question.question_id?.trim()) {
      errors.push('Question ID is required')
    }
    
    if (!question.question_text?.trim()) {
      errors.push('Question text is required')
    }
    
    if (!question.question_type) {
      errors.push('Question type is required')
    }
    
    // Validate question ID format (should be alphanumeric with possible underscores/hyphens)
    if (question.question_id && !/^[a-zA-Z0-9_-]+$/.test(question.question_id)) {
      errors.push('Question ID should only contain letters, numbers, underscores, and hyphens')
    }
    
    return errors
  }

  const addNewQuestion = () => {
    // Process answer options from raw input during submission
    const processedOptions = (newQuestion.answer_options_raw || '')
      .split(',')
      .map(option => option.trim())
      .filter(option => option.length > 0)
    
    // Create question with processed options for validation
    const questionToValidate = {
      ...newQuestion,
      answer_options: processedOptions
    }
    
    const validationErrors = validateQuestion(questionToValidate)
    
    if (validationErrors.length > 0) {
      onError?.(validationErrors.join(', '))
      return
    }

    // Check for duplicate ID
    if (questions.some(q => q.question_id === newQuestion.question_id)) {
      onError?.('Question ID already exists')
      return
    }

    const question: ExtractedQuestion = {
      question_id: newQuestion.question_id!,
      section: newQuestion.section || '',
      base: newQuestion.base || 'All',
      question_type: newQuestion.question_type as QuestionType,
      question_text: newQuestion.question_text!,
      answer_options: processedOptions,
      programming_logic: newQuestion.programming_logic || '',
      special_instructions: newQuestion.special_instructions || ''
    }

    setQuestions([...questions, { ...question, isSelected: true }])
    setHasUnsavedChanges(true)
    setShowAddDialog(false)
    setNewQuestion({
      question_id: '',
      section: '',
      base: 'All',
      question_type: 'single_choice',
      question_text: '',
      answer_options: [],
      answer_options_raw: '',
      programming_logic: '',
      special_instructions: '',
      notes: ''
    })
  }

  const saveAllChanges = async () => {
    setIsSaving(true)
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/questions/${sessionId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questions: questions
            .filter(q => q.isSelected !== false) // Include selected questions
            .map(q => ({
              question_id: q.question_id,
              section: q.section,
              base: q.base,
              question_type: q.question_type,
              question_text: q.question_text,
              answer_options: q.answer_options,
              programming_logic: q.programming_logic,
              special_instructions: q.special_instructions,
              notes: (q as EditingQuestion).notes || '',
              isSelected: q.isSelected
            }))
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to save changes')
      }

      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to save changes')
      }

      setHasUnsavedChanges(false)
      onQuestionsUpdated?.(questions.filter(q => q.isSelected !== false))

    } catch (error) {
      console.error('Save error:', error)
      onError?.(error instanceof Error ? error.message : 'Failed to save changes')
    } finally {
      setIsSaving(false)
    }
  }

  const updateAnswerOption = (questionId: string, index: number, value: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { 
            ...q, 
            answer_options: Array.isArray(q.answer_options) 
              ? q.answer_options.map((opt, i) => i === index ? value : opt)
              : q.answer_options,
            hasChanges: true 
          }
        : q
    ))
    setHasUnsavedChanges(true)
  }

  const addAnswerOption = (questionId: string) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { 
            ...q, 
            answer_options: Array.isArray(q.answer_options) ? [...q.answer_options, ''] : [''],
            hasChanges: true 
          }
        : q
    ))
    setHasUnsavedChanges(true)
  }

  const removeAnswerOption = (questionId: string, index: number) => {
    setQuestions(prev => prev.map(q => 
      q.question_id === questionId 
        ? { 
            ...q, 
            answer_options: Array.isArray(q.answer_options) 
              ? q.answer_options.filter((_, i) => i !== index)
              : q.answer_options,
            hasChanges: true 
          }
        : q
    ))
    setHasUnsavedChanges(true)
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review and Edit Questions
      </Typography>
      
      <Typography color="text.secondary" paragraph>
        Review the extracted questions and make any necessary edits. You can modify question text, 
        types, answer options, and add additional questions as needed. Use the checkboxes to select 
        which questions to include in your survey - all questions are selected by default.
      </Typography>

      {/* Dictionary Merge Statistics */}
      {mergeStats && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            📊 Dictionary Enhancement Results
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip 
              label={`${mergeStats.totalQuestions} Total Questions`} 
              color="primary" 
              size="small"
            />
            {mergeStats.validDictionaryUsed && mergeStats.questionnaireOnly > 0 && (
              <Chip 
                label={`${mergeStats.questionnaireOnly} not available in dictionary`} 
                color="warning" 
                size="small"
                variant="outlined"
              />
            )}
            <Chip 
              label={`${mergeStats.dictionaryEnhanced} Dictionary Enhanced`} 
              color="success" 
              size="small"
            />
            <Chip 
              label={`${mergeStats.questionnaireOnly} Questionnaire Only`} 
              color="warning" 
              size="small"
            />
            {mergeStats.dictionaryOnly > 0 && (
              <Chip 
                label={`${mergeStats.dictionaryOnly} Dictionary Only`} 
                color="info" 
                size="small"
              />
            )}
          </Stack>
        </Alert>
      )}

      {/* Filter Chips */}
      {mergeStats?.validDictionaryUsed && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
            Filter Questions
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`All (${questions.length})`}
              onClick={() => setQuestionFilter('all')}
              color={questionFilter === 'all' ? 'primary' : 'default'}
              variant={questionFilter === 'all' ? 'filled' : 'outlined'}
              size="small"
              clickable
            />
            <Chip
              icon={<CheckIcon />}
              label={`Dictionary Enhanced (${questions.filter(q => (q as any).data_source === 'merged' || (q as any).data_source === 'dictionary').length})`}
              onClick={() => setQuestionFilter('dictionary')}
              color={questionFilter === 'dictionary' ? 'success' : 'default'}
              variant={questionFilter === 'dictionary' ? 'filled' : 'outlined'}
              size="small"
              clickable
            />
            <Chip
              icon={<CloseIcon />}
              label={`Questionnaire Only (${questions.filter(q => (q as any).data_source === 'questionnaire' || !(q as any).data_source).length})`}
              onClick={() => setQuestionFilter('questionnaire')}
              color={questionFilter === 'questionnaire' ? 'error' : 'default'}
              variant={questionFilter === 'questionnaire' ? 'filled' : 'outlined'}
              size="small"
              clickable
              sx={{
                '&.MuiChip-colorError': {
                  backgroundColor: questionFilter === 'questionnaire' ? 'error.main' : 'transparent',
                  color: questionFilter === 'questionnaire' ? 'error.contrastText' : 'error.main',
                  borderColor: 'error.main'
                }
              }}
            />
          </Stack>
        </Box>
      )}


      {/* Action Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle1">
              {questions.filter(q => q.isSelected !== false).length} of {questions.length} Questions Selected
            </Typography>
            {hasUnsavedChanges && (
              <Chip 
                icon={<WarningIcon />}
                label="Unsaved Changes" 
                color="warning" 
                size="small" 
              />
            )}
          </Stack>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
            >
              Add Question
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveAllChanges}
              disabled={!hasUnsavedChanges || isSaving || questions.filter(q => q.isSelected !== false).length === 0}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Questions List - Grouped by Section */}
      <Stack spacing={3}>
        {Object.entries(groupedQuestions).map(([sectionName, sectionQuestions]) => {
          // Skip "Other" section if it's empty
          if (sectionName === 'Other' && sectionQuestions.length === 0) {
            return null
          }
          
          return (
            <Paper key={sectionName} elevation={2} sx={{ overflow: 'hidden' }}>
              {/* Section Header */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: sectionName === 'Other' ? 'grey.50' : 'primary.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <IconButton
                    onClick={() => toggleSectionExpanded(sectionName)}
                    size="small"
                  >
                    <ExpandMoreIcon 
                      sx={{ 
                        transform: isSectionExpanded(sectionName) ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s'
                      }} 
                    />
                  </IconButton>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      flex: 1,
                      color: sectionName === 'Other' ? 'text.secondary' : 'primary.main',
                      fontWeight: 600
                    }}
                  >
                    {sectionName === 'Other' ? 'Other Questions' : sectionName}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      label={`${sectionQuestions.filter(q => q.isSelected !== false).length}/${sectionQuestions.length} selected`}
                      size="small"
                      color={sectionName === 'Other' ? 'default' : 'primary'}
                      variant="outlined"
                    />
                    <Button
                      size="small"
                      onClick={() => toggleAllQuestionsInSection(sectionQuestions, true)}
                      disabled={sectionQuestions.every(q => q.isSelected !== false)}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => toggleAllQuestionsInSection(sectionQuestions, false)}
                      disabled={sectionQuestions.every(q => q.isSelected === false)}
                    >
                      Deselect All
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              {/* Section Questions */}
              {isSectionExpanded(sectionName) && (
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {sectionQuestions.map((question) => (
          <Accordion 
            key={question.question_id} 
            defaultExpanded={false}
            sx={{
              opacity: question.isSelected === false ? 0.6 : 1,
              backgroundColor: question.isSelected === false ? 'grey.50' : 'transparent'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={question.isSelected !== false}
                      onChange={(e) => {
                        e.stopPropagation()
                        toggleQuestionSelection(question.question_id)
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 3,
                    py: 1,
                    borderRadius: '16px', // More rounded like chips
                    backgroundColor: question.isSelected === false ? 'grey.200' : 'grey.100',
                    border: '1px solid',
                    borderColor: question.isSelected === false ? 'grey.400' : 'grey.300',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: question.isSelected === false ? 'text.disabled' : 'text.primary',
                    letterSpacing: '1px'
                  }}
                >
                  {question.question_id}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip 
                  label={getQuestionTypeLabel(question.question_type)} 
                  size="small" 
                  color={question.isSelected === false ? 'default' : getQuestionTypeColor(question.question_type)}
                />
                {/* Data Source Indicator */}
                {(question as any).data_source && (
                  <Chip 
                    icon={
                      (question as any).data_source === 'merged' || (question as any).data_source === 'dictionary'
                        ? <CheckIcon />
                        : <CloseIcon />
                    }
                    label={
                      (question as any).data_source === 'merged' 
                        ? 'Dictionary Enhanced'
                        : (question as any).data_source === 'dictionary' 
                          ? 'Dictionary Only'
                          : 'Questionnaire Only'
                    }
                    size="small" 
                    color={
                      (question as any).data_source === 'merged' 
                        ? 'success'
                        : (question as any).data_source === 'dictionary' 
                          ? 'info'
                          : 'error'
                    }
                    variant="outlined"
                    sx={{
                      '&.MuiChip-colorError': {
                        borderColor: 'error.main',
                        color: 'error.main',
                        '& .MuiChip-icon': {
                          color: 'error.main'
                        }
                      }
                    }}
                  />
                )}
                {question.category && (
                  <Chip 
                    label={`📊 ${question.category}`} 
                    size="small" 
                    color={question.isSelected === false ? 'default' : 'secondary'} 
                    variant="outlined"
                  />
                )}
                {question.question_pattern === 'scale' && (
                  <Chip 
                    label="Scale" 
                    size="small" 
                    color={question.isSelected === false ? 'default' : 'info'} 
                  />
                )}
                {question.anchor_options && question.anchor_options.length > 0 && (
                  <Chip 
                    label="Has Anchors" 
                    size="small" 
                    color={question.isSelected === false ? 'default' : 'warning'} 
                  />
                )}
                {question.hasChanges && (
                  <Chip 
                    label="Modified" 
                    size="small" 
                    color={question.isSelected === false ? 'default' : 'warning'} 
                  />
                )}
                {question.isSelected === false && (
                  <Chip 
                    label="Deselected" 
                    size="small" 
                    color="error" 
                    variant="outlined"
                  />
                )}
              </Stack>
            </AccordionSummary>
            
            <AccordionDetails>
              <Card variant="outlined">
                <CardContent>
                  {question.isEditing ? (
                    <Stack spacing={3}>
                      {/* Question Selection */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={question.isSelected !== false}
                            onChange={() => toggleQuestionSelection(question.question_id)}
                          />
                        }
                        label="Include this question in the survey"
                      />

                      {/* Question ID */}
                      <TextField
                        label="Question ID"
                        value={getFieldValue(question, 'question_id')}
                        onChange={(e) => updateQuestion(question.question_id, 'question_id', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'question_id', e.target.value)}
                        size="small"
                        fullWidth
                        required
                        error={validationErrors[question.question_id]?.some(err => err.includes('Question ID'))}
                        helperText={validationErrors[question.question_id]?.find(err => err.includes('Question ID'))}
                      />

                      {/* Question Text */}
                      <TextField
                        label="Question Text"
                        value={getFieldValue(question, 'question_text')}
                        onChange={(e) => updateQuestion(question.question_id, 'question_text', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'question_text', e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        error={validationErrors[question.question_id]?.some(err => err.includes('Question text'))}
                        helperText={validationErrors[question.question_id]?.find(err => err.includes('Question text'))}
                      />

                      {/* Question Type */}
                      <FormControl 
                        size="small" 
                        sx={{ minWidth: 200 }}
                        required
                        error={validationErrors[question.question_id]?.some(err => err.includes('Question type'))}
                      >
                        <InputLabel>Question Type *</InputLabel>
                        <Select
                          value={getFieldValue(question, 'question_type')}
                          label="Question Type *"
                          onChange={(e) => {
                            updateQuestion(question.question_id, 'question_type', e.target.value)
                            saveEditingValue(question.question_id, 'question_type', e.target.value)
                          }}
                        >
                          {questionTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Base */}
                      <TextField
                        label="Base"
                        value={getFieldValue(question, 'base') || 'All'}
                        onChange={(e) => updateQuestion(question.question_id, 'base', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'base', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="e.g., All, Males, Females, Age 18-34"
                        helperText="Target audience or filter for this question"
                      />

                      {/* Answer Options */}
                      {(question.question_type === 'single_choice' || question.question_type === 'multi_choice' || question.question_type === 'single_choice_grid' || question.question_type === 'multiple_choice_grid') && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Answer Options
                          </Typography>
                          <Stack spacing={1}>
                            {(Array.isArray(question.answer_options) ? question.answer_options : []).map((option, optIndex) => (
                              <Stack key={optIndex} direction="row" spacing={1} alignItems="center">
                                <TextField
                                  value={option}
                                  onChange={(e) => updateAnswerOption(question.question_id, optIndex, e.target.value)}
                                  size="small"
                                  fullWidth
                                  placeholder={`Option ${optIndex + 1}`}
                                />
                                <IconButton
                                  onClick={() => removeAnswerOption(question.question_id, optIndex)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => addAnswerOption(question.question_id)}
                              size="small"
                              variant="outlined"
                            >
                              Add Option
                            </Button>
                          </Stack>
                        </Box>
                      )}

                      {/* Statements for grid questions */}
                      {(question.question_type === 'single_choice_grid' || question.question_type === 'multiple_choice_grid') && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Statements to Evaluate
                          </Typography>
                          <Stack spacing={1}>
                            {(question.statements || []).map((statement, stmtIndex) => (
                              <Stack key={stmtIndex} direction="row" spacing={1} alignItems="center">
                                <TextField
                                  value={statement}
                                  onChange={(e) => {
                                    const newStatements = [...(question.statements || [])]
                                    newStatements[stmtIndex] = e.target.value
                                    updateQuestion(question.question_id, 'statements', newStatements)
                                  }}
                                  size="small"
                                  fullWidth
                                  placeholder={`Statement ${stmtIndex + 1}`}
                                />
                                <IconButton
                                  onClick={() => {
                                    const newStatements = (question.statements || []).filter((_, i) => i !== stmtIndex)
                                    updateQuestion(question.question_id, 'statements', newStatements)
                                  }}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            ))}
                            <Button
                              startIcon={<AddIcon />}
                              onClick={() => {
                                const newStatements = [...(question.statements || []), '']
                                updateQuestion(question.question_id, 'statements', newStatements)
                              }}
                              size="small"
                              variant="outlined"
                            >
                              Add Statement
                            </Button>
                          </Stack>
                        </Box>
                      )}

                      {/* Section */}
                      <TextField
                        label="Section"
                        value={getFieldValue(question, 'section')}
                        onChange={(e) => updateQuestion(question.question_id, 'section', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'section', e.target.value)}
                        size="small"
                        fullWidth
                      />

                      {/* Programming Logic */}
                      <TextField
                        label="Programming Logic"
                        value={getFieldValue(question, 'programming_logic')}
                        onChange={(e) => updateQuestion(question.question_id, 'programming_logic', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'programming_logic', e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="e.g., IF Q1=1 THEN ASK"
                      />

                      {/* Special Instructions */}
                      <TextField
                        label="Special Instructions"
                        value={getFieldValue(question, 'special_instructions')}
                        onChange={(e) => updateQuestion(question.question_id, 'special_instructions', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'special_instructions', e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Any special instructions for this question"
                      />

                      {/* Question Pattern */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Question Pattern</InputLabel>
                        <Select
                          value={getFieldValue(question, 'question_pattern') || 'standard'}
                          onChange={(e) => {
                            updateQuestion(question.question_id, 'question_pattern', e.target.value)
                            saveEditingValue(question.question_id, 'question_pattern', e.target.value)
                          }}
                          label="Question Pattern"
                        >
                          <MenuItem value="standard">Standard</MenuItem>
                          <MenuItem value="scale">Scale</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Anchor Options */}
                      <TextField
                        label="Anchor Options"
                        value={getFieldValue(question, 'anchor_options')?.join(', ') || question.anchor_options?.join(', ') || ''}
                        onChange={(e) => updateQuestion(question.question_id, 'anchor_options', 
                          e.target.value ? e.target.value.split(',').map(s => s.trim()) : [])}
                        onBlur={(e) => saveEditingValue(question.question_id, 'anchor_options', 
                          e.target.value ? e.target.value.split(',').map(s => s.trim()) : [])}
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Options with [ANCHOR], [FIXED] notation (comma-separated)"
                        helperText="Enter anchor options separated by commas"
                      />

                      {/* Special Instructions Extracted */}
                      <TextField
                        label="Extracted Instructions"
                        value={getFieldValue(question, 'special_instructions_extracted') || ''}
                        onChange={(e) => updateQuestion(question.question_id, 'special_instructions_extracted', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'special_instructions_extracted', e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Abstract instructions not directly stated in document"
                        helperText="Instructions inferred from question pattern or best practices"
                      />

                      {/* Notes */}
                      <TextField
                        label="Notes"
                        value={getFieldValue(question, 'notes') || ''}
                        onChange={(e) => updateQuestion(question.question_id, 'notes', e.target.value)}
                        onBlur={(e) => saveEditingValue(question.question_id, 'notes', e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                        placeholder="Add notes about this question"
                      />
                    </Stack>
                  ) : (
                    <Stack spacing={2}>
                      {/* Selection Status */}
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: question.isSelected === false ? 'error.50' : 'success.50',
                          border: '1px solid',
                          borderColor: question.isSelected === false ? 'error.200' : 'success.200',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Checkbox
                          checked={question.isSelected !== false}
                          onChange={() => toggleQuestionSelection(question.question_id)}
                          size="small"
                        />
                        <Typography 
                          variant="body2" 
                          color={question.isSelected === false ? 'error.main' : 'success.main'}
                          fontWeight={600}
                        >
                          {question.isSelected === false ? 'Question will be excluded from survey' : 'Question will be included in survey'}
                        </Typography>
                      </Box>

                      {/* Individual Question Warnings */}
                      {(question as any).warnings && (question as any).warnings.length > 0 && (
                        <Alert severity="warning">
                          <Typography variant="subtitle2" gutterBottom>
                            Dictionary Processing Notes
                          </Typography>
                          <Stack spacing={0.5}>
                            {(question as any).warnings.map((warning: string, index: number) => (
                              <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                • {warning}
                              </Typography>
                            ))}
                          </Stack>
                        </Alert>
                      )}

                      {/* Source Comparison for Dictionary Enhanced Questions */}
                      {(question as any).source_comparison && (question as any).data_source === 'merged' && (
                        <Alert severity="info">
                          <Typography variant="subtitle2" gutterBottom>
                            📖 Source Comparison (Dictionary Enhanced)
                          </Typography>
                          <Stack spacing={1}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                <strong>Question ID:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                📖 Dictionary: {(question as any).source_comparison.question_id.dictionary || 'None'}
                              </Typography>
                              <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                📝 Questionnaire: {(question as any).source_comparison.question_id.questionnaire || 'None'}
                              </Typography>
                            </Box>
                            
                            {((question as any).source_comparison.question_text.dictionary || (question as any).source_comparison.question_text.questionnaire) && (
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Question Text:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                  📖 Dictionary: {(question as any).source_comparison.question_text.dictionary || 'None'}
                                </Typography>
                                <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                  📝 Questionnaire: {(question as any).source_comparison.question_text.questionnaire || 'None'}
                                </Typography>
                              </Box>
                            )}

                            {((question as any).source_comparison.answer_options.dictionary || (question as any).source_comparison.answer_options.questionnaire) && (
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Answer Options:</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                  📖 Dictionary: {(question as any).source_comparison.answer_options.dictionary ? `${(question as any).source_comparison.answer_options.dictionary.length} options` : 'None'}
                                </Typography>
                                <Typography variant="body2" sx={{ pl: 1, fontSize: '0.85rem' }}>
                                  📝 Questionnaire: {(question as any).source_comparison.answer_options.questionnaire ? `${(question as any).source_comparison.answer_options.questionnaire.length} options` : 'None'}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Alert>
                      )}

                      <Typography variant="body1">
                        <strong>Question:</strong> {question.question_text}
                      </Typography>
                      
                      {Array.isArray(question.answer_options) && question.answer_options.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Answer Options:
                          </Typography>
                          <Stack spacing={0.5}>
                            {question.answer_options.map((option, optIndex) => (
                              <Typography key={optIndex} variant="body2" sx={{ pl: 2 }}>
                                {optIndex + 1}. {option}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {question.statements && question.statements.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Statements to Evaluate:
                          </Typography>
                          <Stack spacing={0.5}>
                            {question.statements.map((statement, stmtIndex) => (
                              <Typography key={stmtIndex} variant="body2" sx={{ pl: 2 }}>
                                {stmtIndex + 1}. {statement}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {question.section && (
                        <Typography variant="body2">
                          <strong>Section:</strong> {question.section}
                        </Typography>
                      )}

                      {question.base && (
                        <Typography variant="body2">
                          <strong>Base:</strong> {question.base}
                        </Typography>
                      )}

                      {question.programming_logic && (
                        <Typography variant="body2">
                          <strong>Programming Logic:</strong> {question.programming_logic}
                        </Typography>
                      )}

                      {question.special_instructions && (
                        <Typography variant="body2">
                          <strong>Special Instructions:</strong> {question.special_instructions}
                        </Typography>
                      )}

                      {question.question_pattern && (
                        <Typography variant="body2">
                          <strong>Question Pattern:</strong> {question.question_pattern}
                        </Typography>
                      )}

                      {question.anchor_options && question.anchor_options.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            <strong>Anchor Options:</strong>
                          </Typography>
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Stack spacing={0.5}>
                              {question.anchor_options.map((anchor, index) => (
                                <Typography key={index} variant="body2">
                                  • {anchor}
                                </Typography>
                              ))}
                            </Stack>
                          </Alert>
                        </Box>
                      )}

                      {question.special_instructions_extracted && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Extracted Instructions:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', pl: 1 }}>
                            {question.special_instructions_extracted}
                          </Typography>
                        </Box>
                      )}

                      {(question as EditingQuestion).notes && (
                        <Typography variant="body2">
                          <strong>Notes:</strong> {(question as EditingQuestion).notes}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </CardContent>
                
                <CardActions>
                  {question.isEditing ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={() => saveQuestion(question.question_id)}
                        size="small"
                        variant="contained"
                      >
                        Save
                      </Button>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={() => cancelEditing(question.question_id)}
                        size="small"
                      >
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => startEditing(question.question_id)}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        onClick={() => deleteQuestion(question.question_id)}
                        size="small"
                        color="error"
                      >
                        Delete
                      </Button>
                    </Stack>
                  )}
                </CardActions>
              </Card>
            </AccordionDetails>
          </Accordion>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          )
        })}
      </Stack>

      {/* No Questions Selected Warning */}
      {questions.filter(q => q.isSelected !== false).length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No questions are currently selected. Please select at least one question to proceed.
        </Alert>
      )}

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Question</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Question ID"
              value={newQuestion.question_id}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, question_id: e.target.value }))}
              size="small"
              fullWidth
              required
              error={!newQuestion.question_id?.trim()}
              helperText={!newQuestion.question_id?.trim() ? 'Question ID is required' : 'Use letters, numbers, underscores, and hyphens only'}
            />
            
            <TextField
              label="Question Text"
              value={newQuestion.question_text}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, question_text: e.target.value }))}
              multiline
              rows={3}
              fullWidth
              required
              error={!newQuestion.question_text?.trim()}
              helperText={!newQuestion.question_text?.trim() ? 'Question text is required' : ''}
            />
            
            <FormControl size="small" fullWidth required>
              <InputLabel>Question Type *</InputLabel>
              <Select
                value={newQuestion.question_type}
                label="Question Type *"
                onChange={(e) => setNewQuestion(prev => ({ ...prev, question_type: e.target.value as QuestionType }))}
              >
                {questionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Base"
              value={newQuestion.base}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, base: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g., All, Males, Females, Age 18-34"
              helperText="Target audience or filter for this question"
            />
            
            {/* Answer Options for choice and grid questions */}
            {(newQuestion.question_type === 'single_choice' || newQuestion.question_type === 'multi_choice' || newQuestion.question_type === 'single_choice_grid' || newQuestion.question_type === 'multiple_choice_grid') && (
              <TextField
                label="Answer Options"
                value={newQuestion.answer_options_raw || ''}
                onChange={(e) => setNewQuestion(prev => ({ ...prev, answer_options_raw: e.target.value }))}
                multiline
                rows={3}
                fullWidth
                placeholder="Enter options separated by commas, e.g., Yes, No, Maybe"
                helperText="Separate each option with a comma. Empty options will be ignored."
              />
            )}
            
            <TextField
              label="Section"
              value={newQuestion.section}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, section: e.target.value }))}
              size="small"
              fullWidth
            />
            
            <TextField
              label="Notes"
              value={newQuestion.notes || ''}
              onChange={(e) => setNewQuestion(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={2}
              fullWidth
              placeholder="Add notes about this question"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button onClick={addNewQuestion} variant="contained">Add Question</Button>
        </DialogActions>
      </Dialog>

      {hasUnsavedChanges && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You have unsaved changes. Make sure to save your changes before proceeding to the next step.
        </Alert>
      )}
    </Box>
  )
}

export default QuestionReviewStep